import React, { ReactElement, ReactNode, useCallback, useEffect, useState } from 'react';
import { usePopper } from 'react-popper';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { OffsetsFunction } from '@popperjs/core/lib/modifiers/offset';
import { useDeviceLayout } from '../../../context/deviceLayout';
import { getLinariaClassName } from '../../../utils/getLinariaClassName';
import { Button } from '../Button';
import { Flex } from '../helpers/Flex';
import { Icon } from '../Icon';
import { Text } from '../Text';
export enum TooltipPlacement {
  Auto = 'auto',
  AutoStart = 'auto-start',
  AutoEnd = 'auto-end',
  Top = 'top',
  TopStart = 'top-start',
  TopEnd = 'top-end',
  Bottom = 'bottom',
  BottomStart = 'bottom-start',
  BottomEnd = 'bottom-end',
  Right = 'right',
  RightStart = 'right-start',
  RightEnd = 'right-end',
  Left = 'left',
  LeftStart = 'left-start',
  LeftEnd = 'left-end',
}
type IconAlignment = 'flex-start' | 'center';
interface Props {
  /** Open or closed state of the Tooltip */
  isOpen?: boolean;
  /** Tooltip title */
  title?: string;
  /** Tooltip content */
  content: ReactNode;
  /** Tooltip placement relative to trigger component */
  placement?: TooltipPlacement;
  /** Trigger component */
  children?: ReactNode;
  /** Handler that fires when the tooltip is shown */
  onRender?: () => void;
  /** Pass through classname to allow styles overrides */
  className?: string;
  /** Whether it should render inline */
  isInline?: boolean;
  /** Anchor element to show Tooltip near it */
  anchor?: Element | null;
  /** Passing this callback will show the close button.
   Pressing the button will cause the callback execution */
  onClose?: () => void;
  /** An icon placed before the Tooltip content. It can be any type of React Element */
  icon?: ReactElement;
  /** Pass through alignIcon to allow vertically center aligned icon for example */
  alignIcon?: IconAlignment;
}
const _exp = /*#__PURE__*/() => Flex.Row;
const TooltipContainer = /*#__PURE__*/styled(_exp())({
  name: "TooltipContainer",
  class: "tv2197y",
  propsAsIs: true
});
const _exp6 = /*#__PURE__*/() => Flex.Row;
const IconWrapper = /*#__PURE__*/styled(_exp6())({
  name: "IconWrapper",
  class: "i6naup1",
  propsAsIs: true
});
const CloseButtonWrapper = /*#__PURE__*/styled('div')({
  name: "CloseButtonWrapper",
  class: "cch7xam",
  propsAsIs: false
});
const StyledTooltip = /*#__PURE__*/styled('div')({
  name: "StyledTooltip",
  class: "s545xf6",
  propsAsIs: false
});
const StyledTriggerWrapper = /*#__PURE__*/styled('div')({
  name: "StyledTriggerWrapper",
  class: "s1ju66r2",
  propsAsIs: false
});
const StyledArrow = /*#__PURE__*/styled('div')({
  name: "StyledArrow",
  class: "sf32jql",
  propsAsIs: false
});
const OFFSET_MAP: Record<TooltipPlacement, [number, number]> = {
  [TooltipPlacement.Auto]: [0, 8],
  [TooltipPlacement.AutoStart]: [-8, 8],
  [TooltipPlacement.AutoEnd]: [8, 8],
  [TooltipPlacement.Top]: [0, 8],
  [TooltipPlacement.TopStart]: [-8, 8],
  [TooltipPlacement.TopEnd]: [8, 8],
  [TooltipPlacement.Bottom]: [0, 8],
  [TooltipPlacement.BottomStart]: [-8, 8],
  [TooltipPlacement.BottomEnd]: [8, 8],
  [TooltipPlacement.Right]: [-8, 0],
  [TooltipPlacement.RightStart]: [-8, 8],
  [TooltipPlacement.RightEnd]: [-8, 8],
  [TooltipPlacement.Left]: [8, 0],
  [TooltipPlacement.LeftStart]: [8, 8],
  [TooltipPlacement.LeftEnd]: [8, 8]
};
export const Tooltip = ({
  children,
  title,
  content,
  placement = TooltipPlacement.Bottom,
  className,
  onRender,
  isInline = true,
  isOpen = false,
  anchor = undefined,
  onClose,
  icon,
  alignIcon = 'flex-start'
}: Props) => {
  const [referenceElement, setReferenceElement] = useState<Element>();
  const [popperElement, setPopperElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);
  const [visible, setVisible] = useState(isOpen);
  const {
    isMobile
  } = useDeviceLayout();
  const hasCloseButton = Boolean(onClose) || isMobile;

  // Needs to be memoized to avoid infinite loop.
  // See: https://popper.js.org/react-popper/v2/faq/#why-i-get-render-loop-whenever-i-put-a-function-inside-the-popper-configuration
  const getOffset = useCallback<OffsetsFunction>(({
    placement
  }) => OFFSET_MAP[placement], []);
  const toggleTooltip = (to: boolean) => () => setVisible(to);
  const handleCloseButtonClick = (e: React.SyntheticEvent) => {
    e.preventDefault();
    onClose?.();
    setVisible(!visible);
  };
  useEffect(() => {
    setVisible(isOpen);
  }, [isOpen]);
  useEffect(() => {
    if (anchor) {
      setReferenceElement(anchor);
    }
  }, [anchor]);
  const {
    styles,
    attributes
  } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [{
      name: 'arrow',
      options: {
        element: arrowElement,
        padding: 6
      }
    }, {
      name: 'offset',
      options: {
        offset: getOffset
      }
    }]
  });
  useEffect(() => {
    if (visible) {
      onRender?.();
    }
  }, [visible, onRender]);
  if (children) return <>
        <StyledTriggerWrapper
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore-error somehow it works correctly when you pass set ref function instead of ref.
    // And it can't be hidden by @ts-expect-error directive because of typechecks from other packages
    ref={anchor ? undefined : setReferenceElement} onMouseEnter={isMobile ? toggleTooltip(false) : toggleTooltip(true)} onFocus={toggleTooltip(true)} onClick={() => setVisible(!visible)} onMouseLeave={toggleTooltip(false)} onBlur={toggleTooltip(false)} className={cx(isInline && 'isInline')}>
          {children}
        </StyledTriggerWrapper>
        {visible && <StyledTooltip
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore-error somehow it works correctly when you pass set ref function instead of ref.
    // And it can't be hidden by @ts-expect-error directive because of typechecks from other packages
    ref={setPopperElement} style={styles.popper} className={className} {...attributes.popper}>
            <TooltipContainer alignItems={alignIcon} className={cx(hasCloseButton && '--has-close-button', icon && '--has-icon')} gap={icon ? 's400' : 's000'}>
              {icon && <IconWrapper alignItems="center">{icon}</IconWrapper>}
              <div>
                <Text variant="labelM" colorPath="tooltip.default.text">
                  {title}
                </Text>
                <Text variant="bodyS" colorPath="tooltip.default.text">
                  {content}
                </Text>
              </div>
              {(isMobile || Boolean(onClose)) && <CloseButtonWrapper>
                  <Button hasTouchArea variant="transparent" size="md" iconStart={<Icon name="Close" size="md" colorPath={`tooltip.default.icon`} />} onClick={handleCloseButtonClick} />
                </CloseButtonWrapper>}
            </TooltipContainer>

            <StyledArrow
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore-error somehow it works correctly when you pass set ref function instead of ref. And it can't be hidden by @ts-expect-error directive because of typechecks from other packages
      ref={setArrowElement} className={cx(`${getLinariaClassName(StyledArrow)}--variant-default`)} style={styles.arrow} />
          </StyledTooltip>}
      </>;
  // If we don't pass the children, we can render the tooltip near the anchor element

  return visible ? <StyledTooltip
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore-error somehow it works correctly when you pass set ref function instead of ref.
  // And it can't be hidden by @ts-expect-error directive because of typechecks from other packages.
  ref={setPopperElement} style={styles.popper} className={className} {...attributes.popper}>
      <TooltipContainer className={cx(hasCloseButton && '--has-close-button')}>
        <div>
          <Text variant="labelM" colorPath="tooltip.default.text">
            {title}
          </Text>
          <Text variant="bodyS" colorPath="tooltip.default.text">
            {content}
          </Text>
        </div>
        {hasCloseButton && <CloseButtonWrapper>
            <Button hasTouchArea variant="transparent" size="md" iconStart={<Icon name="Close" size="md" colorPath={`tooltip.default.icon`} />} onClick={handleCloseButtonClick} />
          </CloseButtonWrapper>}
      </TooltipContainer>
      <TooltipContainer> {content}</TooltipContainer>

      <StyledArrow
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore-error somehow it works correctly when you pass set ref function instead of ref. And it can't be hidden by @ts-expect-error directive because of typechecks from other packages
    ref={setArrowElement} className={cx(`${getLinariaClassName(StyledArrow)}--variant-default`)} style={styles.arrow} />
    </StyledTooltip> : null;
};

require("./Tooltip.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./Tooltip.tsx");